/**
 * usePartnerCompanySelect Hook (T042 - US2)
 * React Query hook for partner company selection with debounced search
 *
 * Features:
 * - Debounced search (300ms)
 * - Lightweight results for dropdown
 * - Caches search results
 */

import { useState, useEffect, useCallback } from 'react';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { partnerCompaniesApi } from '../api/partnerCompaniesApi';
import { partnerCompanyKeys } from './usePartnerCompanies';
import type { PartnerCompanyOption } from '../types/partner-company.types';

/** Debounce delay in milliseconds */
const DEBOUNCE_DELAY = 300;

/** Minimum query length to trigger search */
const MIN_QUERY_LENGTH = 1;

/**
 * Hook for partner company selection with debounced search
 *
 * @param initialValue - Initial selected company (optional)
 * @returns Object with search state, results, and selection handlers
 *
 * @example
 * const {
 *   searchQuery,
 *   setSearchQuery,
 *   options,
 *   isLoading,
 *   selectedCompany,
 *   selectCompany,
 *   syncSelectedCompany,
 *   clearSelection,
 * } = usePartnerCompanySelect(currentCompany);
 */
export function usePartnerCompanySelect(
  initialValue?: PartnerCompanyOption | null,
) {
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [selectedCompany, setSelectedCompanyState] = useState<PartnerCompanyOption | null>(
    initialValue || null,
  );

  // Debounce the search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, DEBOUNCE_DELAY);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch search results
  const {
    data: options = [],
    isLoading,
    isFetching,
  }: UseQueryResult<PartnerCompanyOption[], Error> = useQuery({
    queryKey: partnerCompanyKeys.search(debouncedQuery),
    queryFn: () =>
      partnerCompaniesApi.searchPartnerCompanies({
        q: debouncedQuery,
        limit: 20,
      }),
    enabled: debouncedQuery.length >= MIN_QUERY_LENGTH,
    staleTime: 60000, // 1 minute
  });

  /**
   * Clear the current selection and search query
   */
  const clearSelection = useCallback(() => {
    setSelectedCompanyState(null);
    setSearchQuery('');
  }, []);

  /**
   * Handle user-initiated company selection (clears search query)
   */
  const selectCompany = useCallback((company: PartnerCompanyOption | null) => {
    setSelectedCompanyState(company);
    setSearchQuery('');
  }, []);

  /**
   * Sync selected company from external value (does NOT clear search query)
   * Use this for controlled component value synchronization
   */
  const syncSelectedCompany = useCallback((company: PartnerCompanyOption | null) => {
    setSelectedCompanyState(company);
  }, []);

  return {
    // Search state
    searchQuery,
    setSearchQuery,
    debouncedQuery,

    // Results
    options,
    isLoading: isLoading || isFetching,

    // Selection state
    selectedCompany,
    selectCompany,
    syncSelectedCompany,
    clearSelection,
  };
}

/**
 * Simpler hook just for fetching search results
 *
 * @param query - Search query string
 * @returns Query result with partner company options
 */
export function usePartnerCompanySearch(
  query: string,
): UseQueryResult<PartnerCompanyOption[], Error> {
  return useQuery({
    queryKey: partnerCompanyKeys.search(query),
    queryFn: () =>
      partnerCompaniesApi.searchPartnerCompanies({
        q: query,
        limit: 20,
      }),
    enabled: query.length >= MIN_QUERY_LENGTH,
    staleTime: 60000,
  });
}
